import type { ProfileModuleDto } from '@up2stay/up2promo-api-client';
import {
  Card,
  CardContent,
  cn,
  previewPanelSurfaceClass,
  Skeleton,
} from '@up2stay/up2promo-ui';
import type { ComponentProps } from 'react';

import {
  PROFILE_PREVIEW_RAIL_WIDTH_CLASS,
  stickyPhonePreviewRailClass,
} from '@/core/components/layout';
import { IPhonePreview } from '@/core/components/ui/iphone-preview';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

type IPhoneProfileData = NonNullable<
  ComponentProps<typeof IPhonePreview>['profileData']
>;

type PreviewUser = {
  username?: string | null;
  photo?: { path?: string } | null;
} | null;

export type ProfilesPreviewPanelProps = {
  isLoading: boolean;
  profile:
    | (IPhoneProfileData & { workspace?: string | null })
    | null
    | undefined;
  user: PreviewUser;
  userData: { name: string };
  workspaceUsername: string | null;
  currentWorkspace: { photo?: { path?: string } | null } | null | undefined;
  previewModules: ProfileModuleDto[];
};

export function ProfilesPreviewPanel({
  isLoading,
  profile,
  user,
  userData,
  workspaceUsername,
  currentWorkspace,
  previewModules,
}: ProfilesPreviewPanelProps) {
  const dict = useDictionary();
  const workspaceId = profile?.workspace ?? null;
  const profileData: ComponentProps<typeof IPhonePreview>['profileData'] =
    profile
      ? {
          id: profile.id,
          title: profile.title || userData.name,
          jobTitle: profile.jobTitle,
          company: profile.company,
          location: profile.location,
          profileImage: profile.profileImage,
          showProfileImage: profile.showProfileImage,
          showUp2promoBranding: profile.showUp2promoBranding,
          styling: profile.styling,
          ownerPhoto: user?.photo?.path ? { path: user.photo.path } : undefined,
          workspacePhoto:
            workspaceId && currentWorkspace?.photo?.path
              ? { path: currentWorkspace.photo.path }
              : undefined,
          modules: previewModules,
          leadForm: profile.leadForm,
        }
      : null;

  return (
    <div
      data-slot="profile-preview-rail"
      className={cn(
        'hidden min-w-0 flex-col gap-4 lg:flex',
        PROFILE_PREVIEW_RAIL_WIDTH_CLASS,
        stickyPhonePreviewRailClass(isCapacitorNative()),
      )}
    >
      <Card
        className={cn(
          'flex min-w-0 flex-col items-center overflow-hidden px-6 py-6',
          previewPanelSurfaceClass,
        )}
      >
        <div className="mb-5 w-full self-start text-[11px] font-semibold uppercase tracking-[0.7px] text-muted-foreground">
          {(dict.profiles as { livePreview?: string }).livePreview ??
            'Live preview'}
        </div>
        <CardContent className="flex min-w-0 items-start justify-center overflow-hidden p-0">
          {isLoading && !profile ? (
            <Skeleton className="aspect-[9/19.5] w-full max-w-[28rem] rounded-[2.5rem] border-8 border-background" />
          ) : (
            <IPhonePreview
              username={user?.username}
              workspaceId={workspaceId}
              workspaceUsername={workspaceUsername}
              profileData={profileData}
              className="w-full"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
